import {effect, inject, makeStateKey, Service, signal, TransferState} from "@angular/core";
import {NgbToastConfig} from "@ng-bootstrap/ng-bootstrap";
import {allEqualsOrdered} from "../helpers/Equatable";
import {type IdentifiableToast, type Toast, ToastType} from "../models/Toast";

const toastsKey = makeStateKey<readonly IdentifiableToast[]>("toasts");

@Service()
export class Toasts {
  private readonly transferState = inject(TransferState);
  private readonly toastsConfig = inject(NgbToastConfig);

  private readonly _toasts = signal(this.transferState.get(toastsKey, []),
    {equal: (a, b) => allEqualsOrdered(a, b, (l, r) => l.id === r.id)});

  public constructor() {
    effect(() => {
      const toasts = this._toasts();
      if (toasts.length > 0) this.transferState.set(toastsKey, toasts);
      else this.transferState.remove(toastsKey);
    });
  }

  public get toasts(): readonly IdentifiableToast[] {
    return this._toasts();
  }

  public show(toast: Toast): void {
    const identifiedToast: IdentifiableToast = {
      id: toast.id ?? crypto.randomUUID(), // use ?? to only generate a new ID if not provided
      autoHideAfterMs: this.toastsConfig.delay, // will be overridden by `toast` if set.
      ...toast,
    };
    this._toasts.update(t => t.concat(identifiedToast));
  }

  public showSuccess(title: string, message?: string): void {
    this.show({
      type: ToastType.Success,
      title, message,
      autoHideAfterMs: 1500,
    });
  }

  public showInfo(title: string, message?: string): void {
    this.show({
      type: ToastType.Info,
      title, message,
      autoHideAfterMs: 5000,
    });
  }

  public showWarning(title: string, message?: string): void {
    this.show({
      type: ToastType.Warning,
      title, message,
      autoHideAfterMs: 7500,
    });
  }

  public showError(title: string, message?: string): void {
    this.show({
      type: ToastType.Error,
      title, message,
      autoHideAfterMs: 15000,
    });
  }

  public remove(toast: IdentifiableToast): void {
    this._toasts.update(t => t.filter(t => t !== toast && t.id !== toast.id));
  }

  public clear(): void {
    this._toasts.set([]);
  }
}
